/**
 * WelcomePage — first-run landing per BP-028 spec §6.2 (Layout A hero).
 *
 * Renders a personalized greeting, three starter-Member cards (filtered by
 * `createdVia === 'starter_signup'`), a 3-up secondary-actions row, and a
 * Skip-to-dashboard link. If the active TenantUser has already been welcomed
 * we redirect straight to `/` — the route lives at `/welcome` and is only
 * meaningful for first-run sessions.
 *
 * Card click opens a MemberDetailModal pre-loaded with the chosen Member's
 * recommended Jobs. Skip dismisses the welcome flag (and routes home).
 */

import { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useMembers } from '@/features/members/hooks';
import { useAuthStore } from '@/stores/auth';
import { t } from '@/lib/i18n';
import type { Member } from '@/features/members/schemas';
import { RunJobSheet } from '@/features/jobs/components/RunJobSheet';
import { StarterMemberCard } from '../components/StarterMemberCard';
import { MemberDetailModal } from '../components/MemberDetailModal';
import { SecondaryActionsRow } from '../components/SecondaryActionsRow';
import { useDismissWelcome } from '../hooks';
import styles from './WelcomePage.module.css';

export function WelcomePage() {
  const navigate = useNavigate();
  const welcomedAt = useAuthStore((s) => s.tenantUser?.welcomedAt);
  const tenantName = useAuthStore((s) => s.tenantUser?.tenantName ?? '');
  const { data, isLoading } = useMembers();
  const dismiss = useDismissWelcome();
  const [openMemberId, setOpenMemberId] = useState<string | null>(null);
  const [runJob, setRunJob] = useState<{ templateId: string; memberId: string } | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);

  if (welcomedAt) return <Navigate to="/" replace />;
  if (isLoading || !data) return <p className={styles.loading}>{t('Loading…')}</p>;

  const starters = data.items.filter((m: Member) => m.createdVia === 'starter_signup');

  function handleSkip() {
    dismiss.mutate(undefined, {
      onSettled: () => {
        void navigate('/');
      },
    });
  }

  const openMember = starters.find((m) => m.id === openMemberId) ?? null;

  return (
    <div className={styles.page}>
      <section className={styles.hero}>
        <p className={styles.eyebrow}>{t('Welcome to Linqage')}</p>
        <h1 className={styles.headline}>
          {t('{name} is ready. Meet your starter team.', { name: tenantName })}
        </h1>
        <p className={styles.sub}>
          {t('Three teammates already on staff — pick one to start with.')}
        </p>
      </section>

      <section className={styles.cards}>
        {starters.slice(0, 3).map((m) => (
          <StarterMemberCard key={m.id} member={m} onClick={() => setOpenMemberId(m.id)} />
        ))}
      </section>

      <SecondaryActionsRow />

      <button type="button" className={styles.skip} onClick={handleSkip}>
        {t('Skip to dashboard')}
      </button>

      {openMember ? (
        <MemberDetailModal
          member={openMember}
          onClose={() => setOpenMemberId(null)}
          onRunJob={(templateId, memberId) => {
            setOpenMemberId(null);
            setRunJob({ templateId, memberId });
            setSheetOpen(true);
          }}
        />
      ) : null}

      {runJob !== null && (
        <RunJobSheet
          open={sheetOpen}
          onOpenChange={setSheetOpen}
          templateId={runJob.templateId}
          prefillMemberId={runJob.memberId}
        />
      )}
    </div>
  );
}
